import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import axios from 'axios';
import { Calendar, Tag, ChevronRight, Rss } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function InsightsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = selectedCat ? `?category=${encodeURIComponent(selectedCat)}` : '';
    axios.get(`${API}/blog/posts${params}`).then(r => { setPosts(r.data.items); setLoading(false); }).catch(() => setLoading(false));
    axios.get(`${API}/blog/categories`).then(r => setCategories(r.data.categories)).catch(() => {});
  }, [selectedCat]);

  return (
    <div className="min-h-screen bg-white" data-testid="insights-page">
      <SEOHead title="Insights | Nuvanta TradePath" description="Supply chain risk insights, trade intelligence analysis, and industry updates from the Nuvanta TradePath team." />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A2533]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-white font-bold text-xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold">TradePath</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-white/70 hover:text-white text-sm">Home</Link>
            <Link to="/pricing" className="text-white/70 hover:text-white text-sm">Pricing</Link>
            <span className="text-white text-sm font-medium">Insights</span>
            <Link to="/signin" className="text-white/70 hover:text-white text-sm border border-white/20 px-4 py-1.5 rounded-lg">Sign in</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 pt-28 pb-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2533]" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>Insights</h1>
            <p className="text-[#4E6974] mt-1">Supply chain risk intelligence and trade analysis</p>
          </div>
          <a href={`${API}/blog/feed.xml`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#0097A7] text-sm hover:underline" data-testid="insights-rss">
            <Rss className="h-4 w-4" /> RSS Feed
          </a>
        </div>

        {/* Category filter */}
        {categories.length > 0 && (
          <div className="flex gap-2 mb-8 flex-wrap">
            <button onClick={() => setSelectedCat('')} className={`px-3 py-1 text-sm rounded-full border transition-colors ${!selectedCat ? 'bg-[#00707C] text-white border-[#0097A7]' : 'text-[#4E6974] border-[#E0E0E0] hover:border-[#0097A7]'}`}>All</button>
            {categories.map(c => (
              <button key={c} onClick={() => setSelectedCat(c)} className={`px-3 py-1 text-sm rounded-full border transition-colors ${selectedCat === c ? 'bg-[#00707C] text-white border-[#0097A7]' : 'text-[#4E6974] border-[#E0E0E0] hover:border-[#0097A7]'}`}>{c}</button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-[#B0BEC5]">Loading...</p>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#4E6974] text-lg mb-2">No articles published yet</p>
            <p className="text-[#B0BEC5] text-sm">Check back soon for supply chain insights and analysis.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {posts.map(post => (
              <article key={post.id} className="bg-white border border-[#E8ECF0] rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/insights/${post.slug}`)} data-testid={`insight-card-${post.slug}`}>
                {post.og_image && <img src={post.og_image} alt={post.title} className="w-full h-48 object-cover" />}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-[#B0BEC5] mb-2">
                    <Tag className="h-3 w-3" /> <span>{post.category}</span>
                    <span className="mx-1">|</span>
                    <Calendar className="h-3 w-3" /> <span>{fmtDate(post.published_at)}</span>
                  </div>
                  <h2 className="text-lg font-bold text-[#0A2533] group-hover:text-[#0097A7] transition-colors mb-2" style={{ fontFamily: "'Trebuchet MS', sans-serif" }}>{post.title}</h2>
                  <p className="text-sm text-[#4E6974] line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center gap-1 text-[#0097A7] text-sm mt-3 font-medium">
                    Read more <ChevronRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <footer className="bg-[#0A2533] py-8">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <p className="text-white/40 text-xs">Nuvanta Solutions Pty Ltd (ABN 61 689 692 484)</p>
          <div className="flex gap-6">
            <Link to="/terms" className="text-white/60 hover:text-white text-xs">Terms of Service</Link>
            <Link to="/privacy" className="text-white/60 hover:text-white text-xs">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
